import { useCallback } from "react";
import axios, { useApiRequest } from "./axios";
import {
  buildInvoiceIssueEndpoint,
  buildInvoiceMarkPaidEndpoint,
  buildInvoicePdfEndpoint,
  buildInvoiceRegeneratePdfEndpoint,
  buildInvoiceSendEndpoint,
  buildInvoiceSendToKsefEndpoint,
  buildPurchaseInvoiceXmlEndpoint,
  INVOICES_BULK_SEND_ENDPOINT,
  INVOICES_BULK_SEND_TO_KSEF_ENDPOINT,
  INVOICES_FROM_SETTLEMENT_ENDPOINT,
  INVOICES_SUMMARY_ENDPOINT,
  PURCHASE_INVOICES_DOWNLOAD_FROM_KSEF_ENDPOINT,
  PURCHASE_INVOICES_DOWNLOAD_STATUS_ENDPOINT,
} from "../consts";
import {
  Invoice,
  InvoiceSummary,
  PurchaseInvoice,
  PurchaseInvoiceDownloadStatus,
} from "../types/invoices";

// The first download of a purchase invoice's XML goes out to KSeF, and
// authenticating there can run well past the default request timeout.
const KSEF_DOCUMENT_TIMEOUT = 60000;

/**
 * Hand fetched file contents to the browser as a download.
 *
 * Deliberately not a plain link to the endpoint: the API is authenticated
 * with a bearer token held in JS, which a browser navigation would not send.
 */
export const saveBlobAs = (data: BlobPart, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.replace(/\//g, "_");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** A failed blob request delivers its JSON error body as a Blob as well. */
const readBlobError = async (errorData: unknown): Promise<any> => {
  if (!(errorData instanceof Blob)) {
    return errorData;
  }
  try {
    return JSON.parse(await errorData.text());
  } catch {
    return undefined;
  }
};

export const useInvoices = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const getSummary = useCallback(async (): Promise<
    InvoiceSummary | undefined
  > => {
    const { data } = await executeRequest(() =>
      axios.get(INVOICES_SUMMARY_ENDPOINT)
    );
    return data;
  }, [executeRequest]);

  const issueInvoice = useCallback(
    async (id: string | number) =>
      await executeRequest(() => axios.post(buildInvoiceIssueEndpoint(id))),
    [executeRequest]
  );

  /**
   * Force a re-render of the stored PDF. The cache is cleared automatically
   * when the invoice changes, but not when the seller's own details do.
   */
  const regenerateInvoicePdf = useCallback(
    async (id: string | number) =>
      await executeRequest(() =>
        axios.post(buildInvoiceRegeneratePdfEndpoint(id))
      ),
    [executeRequest]
  );

  const sendInvoice = useCallback(
    async (id: string | number, email?: string) =>
      await executeRequest(() =>
        axios.post(buildInvoiceSendEndpoint(id), email ? { email } : {})
      ),
    [executeRequest]
  );

  const sendInvoiceToKsef = useCallback(
    async (id: string | number) =>
      await executeRequest(() =>
        axios.post(buildInvoiceSendToKsefEndpoint(id))
      ),
    [executeRequest]
  );

  /**
   * Queue a selection for KSeF. The response lists what was queued and what
   * was skipped with the reason, as a BulkInvoiceActionResult.
   */
  const bulkSendInvoicesToKsef = useCallback(
    async (ids: number[]) =>
      await executeRequest(() =>
        axios.post(INVOICES_BULK_SEND_TO_KSEF_ENDPOINT, { ids })
      ),
    [executeRequest]
  );

  /**
   * Queue a selection to be emailed to the clients. Invoices already sent are
   * skipped unless `includeAlreadySent` is set.
   */
  const bulkSendInvoices = useCallback(
    async (ids: number[], includeAlreadySent: boolean) =>
      await executeRequest(() =>
        axios.post(INVOICES_BULK_SEND_ENDPOINT, {
          ids,
          include_already_sent: includeAlreadySent,
        })
      ),
    [executeRequest]
  );

  const markInvoicePaid = useCallback(
    async (id: string | number, isPaid: boolean) =>
      await executeRequest(() =>
        axios.patch(buildInvoiceMarkPaidEndpoint(id), { is_paid: isPaid })
      ),
    [executeRequest]
  );

  const createInvoiceFromSettlement = useCallback(
    async (settlementId: string | number) =>
      await executeRequest(() =>
        axios.post(INVOICES_FROM_SETTLEMENT_ENDPOINT, {
          settlement_id: settlementId,
        })
      ),
    [executeRequest]
  );

  const downloadInvoicePdf = useCallback(
    async (id: string | number, invoice?: Invoice) => {
      const { data, success } = await executeRequest(() =>
        axios.get(buildInvoicePdfEndpoint(id), { responseType: "blob" })
      );

      if (!success || !data) {
        return { success: false };
      }

      saveBlobAs(data, `${invoice?.number || `faktura-${id}`}.pdf`);
      return { success: true };
    },
    [executeRequest]
  );

  return {
    loading,
    error,
    getSummary,
    issueInvoice,
    regenerateInvoicePdf,
    sendInvoice,
    sendInvoiceToKsef,
    bulkSendInvoicesToKsef,
    bulkSendInvoices,
    markInvoicePaid,
    createInvoiceFromSettlement,
    downloadInvoicePdf,
  };
};

export const usePurchaseInvoices = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const getDownloadStatus = useCallback(async (): Promise<
    PurchaseInvoiceDownloadStatus | undefined
  > => {
    const { data } = await executeRequest(() =>
      axios.get(PURCHASE_INVOICES_DOWNLOAD_STATUS_ENDPOINT)
    );
    return data ?? undefined;
  }, [executeRequest]);

  /** Queue retrieval of new purchase invoices; it runs in the background. */
  const downloadFromKsef = useCallback(
    async () =>
      await executeRequest(() =>
        axios.post(PURCHASE_INVOICES_DOWNLOAD_FROM_KSEF_ENDPOINT)
      ),
    [executeRequest]
  );

  const downloadPurchaseInvoiceXml = useCallback(
    async (id: string | number, invoice?: PurchaseInvoice) => {
      const { data, success, errorData } = await executeRequest(() =>
        axios.get(buildPurchaseInvoiceXmlEndpoint(id), {
          responseType: "blob",
          timeout: KSEF_DOCUMENT_TIMEOUT,
        })
      );

      if (!success || !data) {
        const parsedError = await readBlobError(errorData);
        return { success: false, message: parsedError?.message as string | undefined };
      }

      saveBlobAs(data, `${invoice?.ksef_number || `faktura-${id}`}.xml`);
      return { success: true };
    },
    [executeRequest]
  );

  return {
    loading,
    error,
    getDownloadStatus,
    downloadFromKsef,
    downloadPurchaseInvoiceXml,
  };
};
